import React from "react";
import { ArrowDownRight } from "lucide-react";
import { useSite, resolveImageUrl } from "../context/SiteContext";
import { LetGoWellnessLockup } from "./marketing-themes";

export default function Hero() {
  const { site } = useSite();
  if (!site) return <section className="min-h-[100vh]" />;

  const heroImg = resolveImageUrl(site.hero_image_url);

  return (
    <section id="top" className="relative min-h-[100vh] flex items-end overflow-hidden">
      <div className="absolute inset-0">
        {heroImg && (
          <img src={heroImg} alt="" className="w-full h-full object-cover" loading="eager" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[#FDFBF7]/60 via-[#FDFBF7]/30 to-[#FDFBF7]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32 pt-56 lg:pt-64 w-full">
        <div className="max-w-4xl fade-up">
          <p className="overline text-[#3A5A40] mb-8" data-testid="hero-overline">
            {site.hero_overline}
          </p>

          {/* Brand lockup — the same "LET GO" + script "Wellness" used on the business card */}
          <div className="mb-10 hidden md:block" data-testid="hero-lockup">
            <LetGoWellnessLockup size="1.4in" stacked />
          </div>
          <div className="mb-8 md:hidden" data-testid="hero-lockup-mobile">
            <LetGoWellnessLockup size="0.9in" stacked />
          </div>

          <h1
            className="font-serif font-light text-[#1C1917] leading-[0.95] tracking-tight text-balance text-5xl sm:text-6xl md:text-7xl lg:text-[110px]"
            data-testid="hero-title"
          >
            {site.hero_title_pre}{" "}
            <em className="italic font-light text-[#3A5A40]">{site.hero_title_em}</em>
            {site.hero_title_post}
          </h1>

          <div className="mt-12 grid md:grid-cols-2 gap-10 max-w-3xl">
            <p className="text-[#57534E] text-base md:text-lg leading-relaxed" data-testid="hero-sub">
              {site.hero_subtitle}
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-4 md:justify-end md:items-end">
              <a
                href="#enroll"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 px-8 py-4 btn-primary text-sm tracking-wide"
              >
                Enroll as a New Patient
                <ArrowDownRight className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <a
                href="#visit"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 px-8 py-4 btn-ghost text-sm tracking-wide"
              >
                Visit the clinic
              </a>
            </div>
          </div>
        </div>

        <div className="mt-20 md:mt-28 grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-10 pt-10 border-t border-[#E2DCD0]">
          {[
            { k: "Available", v: "Licensed therapists" },
            { k: "Caring", v: "Patients supported" },
            { k: "Trusted", v: "By the community" },
            { k: "Rooted in", v: "Waterbury, CT" },
          ].map((s) => (
            <div key={s.v} data-testid={`hero-stat-${s.v.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="font-serif text-3xl md:text-4xl text-[#1C1917]">{s.k}</div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#57534E] mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
